/* Where the platform has not looked yet: unmeasured datasets, by source.
 *
 * **The panel that answers what the attention list cannot.** An unmeasured dataset produces no
 * incident, no anomaly and no finding, so a steward who clears every card in "Needs attention" has
 * not finished if a source has never been scored. This is where that gap lives.
 *
 * **Bar length compares sources by how many unmeasured datasets each holds** — the widest is the most
 * — exactly as `unmeasuredBySource` computes it, so the rows stay sorted worst-first. Unmeasured is
 * not failing, which is why the bar is the Overview's neutral data colour rather than a status hue.
 * A source whose scored count could not be read gets the hatched track the product uses for "no
 * reading here", because an empty track and a measured zero are the same picture.
 *
 * Five rows first, the rest one click away, and every count stated in words beside its bar.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useConnectionNames, useConnectionSummaries } from '@/entities/connection';
import { SCREEN_PATHS } from '@/shared/routes';
import { useReveal } from '@/shared/ui/motion';

import { unmeasuredBySource, unmeasuredTotal } from '../model/unmeasured';
import type { UnmeasuredRow } from '../model/unmeasured';
import { Panel, Skeleton } from './OverviewParts';

const COVERAGE_PREVIEW = 5;

function Row({ row }: { readonly row: UnmeasuredRow }): ReactElement {
  const { t } = useTranslation();
  const reading = row.reading;
  return (
    <li className="lq-ov-coverage__row">
      <span
        className="lq-ov-coverage__name"
        title={
          row.name.kind === 'named' ? undefined : t(`catalog.group.${row.name.kind}`)
        }
      >
        {row.name.kind === 'named' ? (
          <Link to={`${SCREEN_PATHS.connections}/${row.connectionId}`}>
            {row.name.name}
          </Link>
        ) : (
          <span className="lq-ov-coverage__unnamed">
            {t(`catalog.group.${row.name.kind}`)}
          </span>
        )}
      </span>
      <span
        className={
          reading.kind === 'notConsulted'
            ? 'lq-ov-coverage__track lq-ov-coverage__track--absent'
            : 'lq-ov-coverage__track'
        }
        title={
          reading.kind === 'allScored'
            ? t('home.unmeasured.allScoredWhy', { total: reading.datasets })
            : reading.kind === 'unmeasured'
              ? undefined
              : t(`home.unmeasured.${reading.kind}Why`)
        }
      >
        {reading.kind === 'unmeasured' && row.share !== null ? (
          <span
            className="lq-ov-coverage__fill"
            style={{ width: `${String(Math.min(100, row.share * 100))}%` }}
            role="img"
            aria-label={t('home.unmeasured.bar', {
              count: reading.count,
              total: reading.datasets,
            })}
          />
        ) : null}
      </span>
      <span className="lq-ov-coverage__count">
        {reading.kind === 'unmeasured'
          ? t('home.unmeasured.count', { count: reading.count })
          : t(`home.unmeasured.${reading.kind}`)}
      </span>
    </li>
  );
}

export function CoveragePanel(): ReactElement {
  const { t } = useTranslation();
  const summaries = useConnectionSummaries();
  const names = useConnectionNames();
  const [all, setAll] = useState(false);

  const rows = summaries.isSuccess
    ? unmeasuredBySource(summaries.data.items, {
        status: names.isPending ? 'loading' : names.isError ? 'unreadable' : 'ready',
        names: names.isSuccess ? names.data : new Map<string, string>(),
      })
    : [];
  const total = summaries.isSuccess ? unmeasuredTotal(summaries.data.items) : null;
  const shown = all ? rows : rows.slice(0, COVERAGE_PREVIEW);

  const list = useReveal<HTMLUListElement>(
    rows.length > 0,
    (root, timeline) => {
      /* A list of sources that all lack a bar — every one fully scored, or not counted — has no
         fill to draw, and an empty target makes GSAP warn. */
      const fills = root.querySelectorAll('.lq-ov-coverage__fill');
      if (fills.length === 0) return;
      timeline.from(fills, {
        scaleX: 0,
        transformOrigin: '0% 50%',
        duration: 0.6,
        stagger: 0.05,
      });
    },
    'coverage',
  );

  return (
    <Panel
      title={t('home.unmeasured.title')}
      help={t('help.metric.unmeasured')}
      subtitle={
        total === null
          ? undefined
          : t('home.unmeasured.subtitle', {
              unmeasured: total.unmeasured,
              datasets: total.datasets,
            })
      }
      busy={summaries.isPending}
      className="lq-ov-coverage"
    >
      {summaries.isPending ? (
        <Skeleton
          label={t('home.unmeasured.loading')}
          lines={['long', 'long', 'medium']}
        />
      ) : summaries.isError ? (
        <p className="lq-ov-note" title={summaries.error.message}>
          {t('home.unmeasured.unreadable')}
        </p>
      ) : rows.length === 0 ? (
        <p className="lq-ov-note">
          {t('home.unmeasured.noSources')}{' '}
          <Link className="lq-ov-link" to={SCREEN_PATHS.connections}>
            {t('home.unmeasured.register')}
          </Link>
        </p>
      ) : (
        <>
          <ul ref={list} className="lq-ov-coverage__list">
            {shown.map((row) => (
              <Row key={row.connectionId} row={row} />
            ))}
          </ul>
          {total !== null && total.notConsulted > 0 ? (
            <p className="lq-ov-coverage__partial">
              {t('home.unmeasured.partial', { count: total.notConsulted })}
            </p>
          ) : null}
          {rows.length > COVERAGE_PREVIEW ? (
            <button
              type="button"
              className="lq-ov-more"
              aria-expanded={all}
              onClick={() => {
                setAll(!all);
              }}
            >
              {all
                ? t('home.unmeasured.showFewer')
                : t('home.unmeasured.showAll', { count: rows.length })}
            </button>
          ) : null}
        </>
      )}
    </Panel>
  );
}
